import React from 'react';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import _get from 'lodash/get';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';

import { Button, Link } from 'src/components/Inputs';
import { Image, imageAdapter } from 'src/components/Layout';
import { CodeSnippet } from 'src/components/Surfaces';
import { RawHtml } from 'src/components/helpers';
import { Tooltip } from '../DataDisplay';

// constants
import { RichTextComponents, ButtonTypes, Contentful } from 'src/lib/constants';

import hyperlinkStyles from './Hyperlink/Hyperlink.module.scss';

// eslint-disable-next-line react/prop-types
const HyperlinkButton = ({ href, trackingId, entryId, children }) => {
  const inspectorProps = useContentfulInspectorMode({ entryId });
  return (
    <Link href={href}>
      <a
        href={href}
        trackingId={trackingId}
        className={hyperlinkStyles.hyperlink}
        {...(entryId ? inspectorProps({ fieldId: 'text' }) : null)}
      >
        {children}
      </a>
    </Link>
  );
};

/**
 * Renders one of Primary|Secondary CTA button inside of a rich text component
 *
 * @param {Object} args
 * @param {Object} args.button - Contentful data model of the button
 * @param {Object} args.classes
 * @param {string=} args.trackingId
 * @returns {<Button/>}
 */
const buttonSwitch = ({ button, classes, trackingId = '' }) => {
  const buttonType = _get(button, 'fields.type');
  const entryId = _get(button, 'sys.id');
  const buttonProps = {
    href: _get(button, 'fields.url'),
    trackingId,
  };
  const buttonLabel = _get(button, 'fields.text');
  switch (buttonType) {
    case ButtonTypes.HYPERLINK:
      return (
        <HyperlinkButton {...buttonProps} entryId={entryId}>
          {buttonLabel}
        </HyperlinkButton>
      );
    case ButtonTypes.PRIMARY_CTA:
      return (
        <Button {...buttonProps} classes={classes} entryId={entryId}>
          {buttonLabel}
        </Button>
      );
    case ButtonTypes.SECONDARY:
    case ButtonTypes.SECONDARY_CTA:
      return (
        <Button secondary {...buttonProps} classes={classes} entryId={entryId}>
          {buttonLabel}
        </Button>
      );
    case ButtonTypes.DEFAULT:
      return (
        <Button {...buttonProps} classes={classes} entryId={entryId}>
          {buttonLabel}
        </Button>
      );
    default:
      throw new Error(
        `Button type does not exist: "${buttonType}" @ EmbeddedEntrySwitch.jsx`,
      );
  }
};

// `renderButton` is an optional hook that lets a consumer fully control how
// embedded CTA buttons render (e.g. the blog renders the v2 themed `Btn`).
// When omitted, the legacy `buttonSwitch` is used, so existing callers are
// unaffected.
const EmbeddedEntrySwitch = (classes = {}, { renderButton } = {}) => {
  return {
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const componentType = _get(
        node,
        Contentful.EMBEDDED_ENTRY_CONTENT_TYPE_ID,
      );
      switch (componentType) {
        case RichTextComponents.BUTTON: {
          const button = node?.data?.target;
          if (renderButton) {
            return renderButton({ button });
          }
          return buttonSwitch({ button, classes });
        }
        case RichTextComponents.IMAGE: {
          return (
            <Image
              {...imageAdapter(node.data.target.fields)}
              classes={classes}
              entryId={node?.data?.target?.sys?.id}
            />
          );
        }
        case RichTextComponents.RAW_HTML:
          return (
            <RawHtml
              body={node?.data?.target?.fields?.body}
              entryId={node?.data?.target?.sys?.id}
            />
          );
        case RichTextComponents.CODE_SNIPPET:
          const { syntax, body } = node?.data?.target?.fields;
          return (
            <CodeSnippet syntax={syntax} entryId={node?.data?.target?.sys?.id}>
              {body}
            </CodeSnippet>
          );
        case RichTextComponents.CTA_BUTTON_WITH_TRACKING: {
          const { trackingId, button } = node?.data?.target?.fields;
          if (renderButton) {
            return renderButton({ button, trackingId });
          }
          return buttonSwitch({ button, classes, trackingId });
        }
        default:
          return null;
      }
    },
    [INLINES.EMBEDDED_ENTRY]: (node) => {
      const componentType = _get(
        node,
        Contentful.EMBEDDED_ENTRY_CONTENT_TYPE_ID,
      );
      switch (componentType) {
        case RichTextComponents.TOOLTIP: {
          const { description, title } = node?.data?.target?.fields;
          return (
            <Tooltip
              description={description}
              title={title}
              entryId={node?.data?.target?.sys?.id}
            />
          );
        }
        default:
          return null;
      }
    },
  };
};

export default EmbeddedEntrySwitch;
